import { useState, useEffect, useRef, useCallback } from "react";
import { getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { fetchDocumentResource } from "@/providers/dataProvider/documentDownload";
import { DocumentTransformation, ExtractedRecord } from "@/types";

// =============================================================================
// Types
// =============================================================================

interface UsePdfSourcePreviewProps {
  documentId: string | undefined;
  transformation: DocumentTransformation | null;
  /** Only fetch the file when true (lazy loading — set true when drawer opens) */
  enabled: boolean;
}

interface UsePdfSourcePreviewReturn {
  /** Raw PDF bytes for passing directly to pdf.js getDocument({ data }) */
  pdfData: Uint8Array | null;
  /** Total page count from processing_metadata.document_pages */
  pageCount: number | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Find the page number for a specific record based on its extracted_fields */
  getPageForRecord: (record: ExtractedRecord) => number | null;
}

// =============================================================================
// Hook
// =============================================================================

export const usePdfSourcePreview = ({
  documentId,
  transformation,
  enabled,
}: UsePdfSourcePreviewProps): UsePdfSourcePreviewReturn => {
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache raw bytes so we only download once per document
  const cacheRef = useRef<{
    documentId: string;
    pdfData: Uint8Array;
  } | null>(null);

  // Fetch the PDF file
  useEffect(() => {
    if (!enabled || !documentId) return;

    // Use cache if available for this document
    if (cacheRef.current?.documentId === documentId) {
      setPdfData(cacheRef.current.pdfData);
      return;
    }

    const abortController = new AbortController();

    const fetchPdf = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const apiUrl = getPartnerAwareApiUrl();
        const url = `${apiUrl}/documents/${documentId}/download`;

        // Partner-scoped, authenticated headers are built centrally so this raw
        // download can't silently drop X-Partner-ID (#5350).
        const response = await fetchDocumentResource(url, { signal: abortController.signal });

        const arrayBuffer = await response.arrayBuffer();

        if (abortController.signal.aborted) return;

        // Store raw bytes — passed directly to pdf.js getDocument({ data })
        const data = new Uint8Array(arrayBuffer);

        // Cache and set state
        cacheRef.current = { documentId, pdfData: data };
        setPdfData(data);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[usePdfSourcePreview] Error:", err);
        setError(err instanceof Error ? err.message : "Failed to load PDF");
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void fetchPdf();

    return () => {
      abortController.abort();
    };
  }, [enabled, documentId]);

  // Page count from processing metadata
  const pageCount = transformation?.processing_metadata?.document_pages ?? null;

  // Find the page number for a specific extracted record
  const getPageForRecord = useCallback((record: ExtractedRecord): number | null => {
    if (!record.extracted_fields) return null;

    for (const field of record.extracted_fields) {
      if (field.source?.page_number !== undefined && field.source.page_number !== null) {
        return field.source.page_number;
      }
    }

    return null;
  }, []);

  return {
    pdfData,
    pageCount,
    isLoading,
    error,
    getPageForRecord,
  };
};
